import { ContentfulImage } from '@ecomm/contentful/components'
import { useLocale } from '@ecomm/data-hooks'
import {
  V2GuidedSystemBuilder as GuidedSystemBuilder,
  type QuoteWizardType
} from '@ecomm/shared-components'
import { safeMapExperiences } from '@ecomm/shared-ninetailed'
import { Experience } from '@ecomm/shared-ninetailed-experience'
import classNames from 'classnames'

import { useHideUntilMounted } from '@ecomm/shared-hooks'
import type { QuoteWizardAsset } from '../../data/QuizBanner/quoteWizardAssetSchema'

export type QuizBannerProps = {
  readonly quoteWizard: QuoteWizardType['quoteWizard']
  readonly image: QuoteWizardAsset
  readonly backgroundColor?: string
  readonly title?: string
  readonly text?: string
}

const DEFAULT_BG = 'bg-[#9AC3DF]'

export function QuizBanner({
  backgroundColor = DEFAULT_BG,
  image,
  quoteWizard,
  title = 'Need help choosing?',
  text = 'We know everyone’s security needs are different. Answer a few questions and we’ll recommend a system that suits you.'
}: QuizBannerProps) {
  const locale = useLocale()

  const classes = useHideUntilMounted('QuizBanner')

  return (
    <div
      className={classNames(
        'relative flex gap-10 overflow-hidden rounded-[20px] p-2 md:px-12 md:py-[34px] md:pb-0',
        backgroundColor,
        classes
      )}
      data-component="QuizBanner"
      data-testid="QuizBanner"
    >
      <div className="absolute bottom-0 left-0 z-0 w-1/2">
        <ContentfulImage
          {...image.quoteWizardAsset}
          className="hidden w-full md:block"
        />
      </div>

      <div className="hidden md:block md:flex-1 z-10">
        <h2 className="mb-4 mt-4 text-5xl font-bold text-[#0F2648] lg:text-[56px] lg:leading-[60px]">
          {title}
        </h2>
        <p className="text-lg text-neutral-black">{text}</p>
      </div>

      <div className="w-full overflow-hidden rounded-[20px] md:mb-[34px] md:flex-1">
        {locale === 'en-US' ? (
          <Experience
            key={quoteWizard.id}
            {...quoteWizard}
            component={GuidedSystemBuilder}
            experiences={safeMapExperiences({
              nt_experiences: quoteWizard.ntExperiencesCollection?.items.map(
                i => ({
                  ...i,
                  variants: i.variants.items
                })
              )
            })}
          />
        ) : (
          <GuidedSystemBuilder {...quoteWizard} />
        )}
      </div>
    </div>
  )
}
